/**
 * A group of boxes with no clean cut between them along one axis.
 */

/**
 * Splits boxes into horizontal bands, ordered top-to-bottom. Each band is a
 * maximal group of boxes connected by row overlap; bands are separated by a row
 * line that no box crosses.
 */
export function splitByHorizontalSlice(boxes) {
  const sorted = boxes.toSorted((a, b) => a.rowTop - b.rowTop);
  const bands = [];
  let current = null;
  let maxBottom = -Infinity;
  for (const box of sorted) {
    if (current === null || box.rowTop > maxBottom) {
      current = [box];
      bands.push(current);
      maxBottom = box.rowBottom;
    } else {
      current.push(box);
      maxBottom = Math.max(maxBottom, box.rowBottom);
    }
  }
  return bands;
}

/**
 * Splits boxes into vertical columns, ordered left-to-right. Each column is a
 * maximal group of boxes connected by column overlap; columns are separated by
 * a column line that no box crosses.
 */
export function splitByVerticalSlice(boxes) {
  const sorted = boxes.toSorted((a, b) => a.columnLeft - b.columnLeft);
  const columns = [];
  let current = null;
  let maxRight = -Infinity;
  for (const box of sorted) {
    if (current === null || box.columnLeft > maxRight) {
      current = [box];
      columns.push(current);
      maxRight = box.columnRight;
    } else {
      current.push(box);
      maxRight = Math.max(maxRight, box.columnRight);
    }
  }
  return columns;
}

/**
 * Decides whether a run of stacked bands should be treated as a set of columns.
 * True when columns created by vertical slices on the row bands intersect with
 * every row band.
 *
 * Example: a button sitting under its card shares that card's column, so the
 * two bands merge and the button stays with the card. A lone element that
 * doesn't line up with the columns above it fails the check and stacks on its
 * own instead of being pulled into a column.
 *
 * Merge ✓ — each column has a box from both bands, so the buttons stay
 * grouped with their cards:
 *
 *         col A     col B
 *       ┌───────┐ ┌───────┐
 *       │ card1 │ │ card2 │   band 1
 *       └───────┘ └───────┘
 *        [btn1]    [btn2]     band 2
 *
 * Merge ✗ — col B has no box from band 2, so the lone button is not pulled
 * into a column and stacks below instead:
 *
 *         col A     col B
 *       ┌───────┐ ┌───────┐
 *       │ card1 │ │ card2 │   band 1
 *       └───────┘ └───────┘
 *              [btn]          band 2
 */
function rowBandsMergeIntoColumns(rowBands) {
  const columns = splitByVerticalSlice(rowBands.flat());
  if (columns.length < 2) {
    return false;
  }

  // Check if each column intersects with every row band
  return columns.every(column => {
    const columnIds = new Set(column.map(box => box.id));
    return rowBands.every(band => band.some(box => columnIds.has(box.id)));
  });
}

/**
 * Groups adjacent bands into column groups, walking top-to-bottom and greedily
 * extending a run while the whole run still merges into columns. Returns the
 * combined boxes of each group — either a single band or a mergeable run of ≥2
 * bands.
 */
function getColumnGroupsFromHorizontalBands(bands) {
  const runs = [];
  for (const band of bands) {
    const currentRun = runs.at(-1);
    // Check if the horizontal bands in the current run can form a logical
    // column group
    if (currentRun && rowBandsMergeIntoColumns([...currentRun, band])) {
      currentRun.push(band);
    } else {
      runs.push([band]);
    }
  }
  return runs.map(run => run.flat());
}

/** Orders boxes by reading order, ie top to bottom, then left to right. */
export function sortBoxesByReadingOrder(boxes) {
  return boxes.toSorted((a, b) => a.rowTop - b.rowTop || a.columnLeft - b.columnLeft);
}

/**
 * Splits into horizontal bands, merges adjacent bands into column groups,
 * then splits each group into columns and orders within each column by reading
 * order.
 */
export function sortBoxesBySlices(boxes) {
  if (boxes.length <= 1) {
    return boxes;
  }
  const bands = splitByHorizontalSlice(boxes);
  const columnGroups = getColumnGroupsFromHorizontalBands(bands);
  return columnGroups.flatMap(splitByVerticalSlice).flatMap(sortBoxesByReadingOrder);
}